/**
 * Calendar-day diff helpers.
 *
 * `Math.ceil((end - now) / MS_PER_DAY)` ticks down at the trial-start
 * hour-of-day, not at midnight — so a user who looks at "X days left" at
 * different times within the same calendar day can see the same number
 * for ~24 hours, which feels like a frozen counter (incident: 2026-04-28).
 *
 * `calendarDaysUntil` aligns the boundary to local midnight, so the
 * displayed count drops by exactly 1 each calendar day in the user's
 * timezone.
 */

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const startOfLocalDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

/**
 * Days from now until the given timestamp, counted in calendar days at
 * local midnight. Returns null for a missing input, 0 if `value` is in the
 * past or today.
 */
export const calendarDaysUntil = (value) => {
  if (!value) return null;
  const end = new Date(value);
  if (Number.isNaN(end.getTime())) return null;
  const diff = startOfLocalDay(end) - startOfLocalDay(new Date());
  if (diff <= 0) return 0;
  return Math.round(diff / MS_PER_DAY);
};

/**
 * Same as calendarDaysUntil but for a window with a known start. Useful
 * when you also need totalDays and dayIndex for progress UI.
 */
export const calendarWindow = (startValue, totalDays) => {
  if (!startValue || !totalDays) return null;
  const start = new Date(startValue);
  if (Number.isNaN(start.getTime())) return null;
  const end = new Date(start.getTime() + totalDays * MS_PER_DAY);
  const daysLeft = calendarDaysUntil(end) ?? 0;
  const dayIndex = Math.min(totalDays, Math.max(1, totalDays - daysLeft + 1));
  return { totalDays, daysLeft, dayIndex };
};
